// client/src/components/ExpertCaseFeed.tsx - Expert Case Feed Component
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

interface CasePost {
    id: string;
    attorney_id: string;
    user_id: string;
    title: string;
    description: string;
    case_type: 'plaintiff' | 'defense' | 'not_specified';
    specialty_needed: string;
    state_needed: string | null;
    urgency: 'standard' | 'urgent' | 'emergency';
    budget_range: string | null;
    timeline: string | null;
    status: 'open' | 'closed' | 'filled';
    created_at: string;
}

export default function ExpertCaseFeed() {
    const [posts, setPosts] = useState<CasePost[]>([]);
    const [loading, setLoading] = useState(true);
    const [expert, setExpert] = useState<any>(null);
    const [selectedPost, setSelectedPost] = useState<CasePost | null>(null);
    const [responseMessage, setResponseMessage] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [myResponses, setMyResponses] = useState<Set<string>>(new Set());

    // Filter state
    const [filterSpecialty, setFilterSpecialty] = useState('');
    const [filterUrgency, setFilterUrgency] = useState('');

    useEffect(() => {
        loadExpertAndPosts();
    }, []);

    const loadExpertAndPosts = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Get expert profile
            const { data: expertData } = await supabase
                .from('experts')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (expertData) {
                setExpert(expertData);
                setFilterSpecialty(expertData.specialty || '');

                if (!expertData.is_verified) {
                    setLoading(false);
                    return;
                }

                await loadPosts();
                await loadMyResponses(expertData.id);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPosts = async () => {
        let query = supabase
            .from('case_posts')
            .select('*')
            .eq('status', 'open')
            .eq('is_active', true)
            .order('created_at', { ascending: false });

        // Only show posts that match this expert's specialty
        if (expert?.specialty) {
            query = query.ilike('specialty_needed', `%${expert.specialty}%`);
        }

        const { data, error } = await query;

        if (error) {
            console.error('Error loading posts:', error);
            return;
        }

        setPosts(data || []);
    };

    const loadMyResponses = async (expertId: string) => {
        const { data } = await supabase
            .from('case_post_responses')
            .select('post_id')
            .eq('expert_id', expertId);

        if (data) {
            setMyResponses(new Set(data.map(r => r.post_id)));
        }
    };

    const handleRespond = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!expert || !selectedPost) return;

        setSubmitting(true);
        setSubmitMessage('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const responseData: any = {
                post_id: selectedPost.id,
                expert_id: expert.id,
                user_id: user.id,
                message: responseMessage,
                is_anonymous: isAnonymous
            };

            if (isAnonymous) {
                responseData.anonymous_id = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }

            // Save to case_post_responses
            const { error } = await supabase
                .from('case_post_responses')
                .insert(responseData);

            if (error) throw error;

            // ALSO create a messages entry so it shows in the conversation thread
            const messageData: any = {
                sender_id: user.id,
                recipient_id: selectedPost.user_id,
                attorney_id: selectedPost.attorney_id,
                expert_id: expert.id,
                sender_type: 'expert',
                message: responseMessage,
                case_title: selectedPost.title,
                case_type: 'not_specified',
                urgency: selectedPost.urgency || 'standard',
                read: false,
                is_conflict_notice: false,
                is_anonymous: isAnonymous,
                created_at: new Date().toISOString(),
            };

            if (isAnonymous) {
                messageData.anonymous_sender_id = responseData.anonymous_id;
                // IMPORTANT: Do NOT null out expert_id - it breaks message threading.
                // The is_anonymous flag controls identity visibility, not expert_id.
            }

            await supabase.from('messages').insert(messageData);

            setSubmitMessage('Response sent successfully!');
            setMyResponses(prev => new Set(Array.from(prev).concat(selectedPost.id)));
            setResponseMessage('');
            setIsAnonymous(false);

            setTimeout(() => {
                setSelectedPost(null);
                setSubmitMessage('');
            }, 2000);
        } catch (error: any) {
            setSubmitMessage('Error sending response: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Filter posts
    const filteredPosts = posts.filter(post => {
        if (filterSpecialty && post.specialty_needed !== filterSpecialty) return false;
        if (filterUrgency && post.urgency !== filterUrgency) return false;
        return true;
    });

    // Get unique specialties for filter
    const specialties = Array.from(new Set(posts.map(p => p.specialty_needed)));

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!expert?.is_verified) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <svg className="w-12 h-12 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Verification Required</h3>
                <p className="text-yellow-700">
                    Your profile must be verified before you can view and respond to case posts.
                    Please wait for admin verification.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Case Opportunities</h2>
                <p className="text-gray-600">Browse cases from attorneys looking for expert witnesses</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Specialty</label>
                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm font-medium">
                        {expert?.specialty || 'Not set'}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                    <select
                        value={filterUrgency}
                        onChange={(e) => setFilterUrgency(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All</option>
                        <option value="standard">Standard</option>
                        <option value="urgent">Urgent</option>
                        <option value="emergency">Emergency</option>
                    </select>
                </div>
                <div className="flex items-end">
                    <button
                        onClick={() => { setFilterUrgency(''); }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Posts Grid */}
            {filteredPosts.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Open Cases</h3>
                    <p className="text-gray-500">Check back later for new case opportunities</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPosts.map((post) => (
                        <div
                            key={post.id}
                            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                        >
                            {/* Urgency Badge */}
                            {post.urgency !== 'standard' && (
                                <span className={`inline-block px-2 py-1 rounded text-xs font-bold mb-3 ${
                                    post.urgency === 'emergency'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-orange-100 text-orange-800'
                                }`}>
                                    {post.urgency.toUpperCase()}
                                </span>
                            )}

                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>
                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.description}</p>

                            <div className="space-y-2 text-sm mb-4">
                                <div className="flex items-center text-gray-600">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    <span className="font-medium">{post.specialty_needed}</span>
                                </div>
                                {post.state_needed && (
                                    <div className="flex items-center text-gray-600">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {post.state_needed}
                                    </div>
                                )}
                                <div className="flex items-center text-gray-600">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                    </svg>
                                    <span className="capitalize">{post.case_type.replace('_', ' ')}</span>
                                </div>
                                {post.budget_range && (
                                    <div className="flex items-center text-gray-600">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {post.budget_range}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <span className="text-xs text-gray-500">
                                    Posted {new Date(post.created_at).toLocaleDateString()}
                                </span>
                                {myResponses.has(post.id) ? (
                                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                                        ✓ Responded
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => setSelectedPost(post)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                    >
                                        Respond
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Response Modal */}
            {selectedPost && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-gray-900">{selectedPost.title}</h3>
                                <button
                                    onClick={() => {
                                        setSelectedPost(null);
                                        setResponseMessage('');
                                        setIsAnonymous(false);
                                        setSubmitMessage('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <p className="text-gray-700 mb-4">{selectedPost.description}</p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Specialty:</span>
                                        <span className="ml-2 font-medium">{selectedPost.specialty_needed}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Case Type:</span>
                                        <span className="ml-2 font-medium capitalize">{selectedPost.case_type.replace('_', ' ')}</span>
                                    </div>
                                    {selectedPost.state_needed && (
                                        <div>
                                            <span className="text-gray-500">State:</span>
                                            <span className="ml-2 font-medium">{selectedPost.state_needed}</span>
                                        </div>
                                    )}
                                    {selectedPost.timeline && (
                                        <div>
                                            <span className="text-gray-500">Timeline:</span>
                                            <span className="ml-2 font-medium">{selectedPost.timeline}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <form onSubmit={handleRespond} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Your Response *
                                    </label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={responseMessage}
                                        onChange={(e) => setResponseMessage(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Introduce yourself and explain why you'd be a good fit for this case..."
                                    />
                                </div>

                                {/* Anonymous Option */}
                                <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                    <label className="flex items-start space-x-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isAnonymous}
                                            onChange={(e) => setIsAnonymous(e.target.checked)}
                                            className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-2 border-gray-400 rounded"
                                        />
                                        <div className="flex-1">
                                            <span className="text-base font-bold text-gray-900">
                                                🔒 Respond Anonymously
                                            </span>
                                            <p className="text-sm text-gray-700 mt-1">
                                                Your name, email, and contact info will NOT be shared.
                                                The attorney will only see your response message.
                                            </p>
                                        </div>
                                    </label>
                                </div>

                                {submitMessage && (
                                    <div className={`p-4 rounded-lg ${
                                        submitMessage.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                    }`}>
                                        {submitMessage}
                                    </div>
                                )}

                                <div className="flex justify-end space-x-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedPost(null);
                                            setResponseMessage('');
                                            setIsAnonymous(false);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {submitting ? 'Sending...' : isAnonymous ? 'Send Anonymous Response' : 'Send Response'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
